import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InterviewSimulator = ({ user, onBack }) => {
  const [currentView, setCurrentView] = useState('menu'); // menu, briefing, simulation, results
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState([]);
  const [showSTARGuide, setShowSTARGuide] = useState(false);
  const chatEndRef = useRef(null);

  const scenarios = [
    {
      id: 'entry_level',
      title: '🎮 Entry-Level Job Interview Simulator',
      difficulty: 'Beginner',
      duration: '8-10 minutes',
      description: 'Enter a realistic virtual job interview and practice responding to common hiring-manager questions.',
      features: ['🤖 AI Hiring Manager', '🏢 Virtual Workplace', '🎯 Interactive Decisions', '📊 Automatic Performance Score'],
      objectives: [
        'Answer common interview questions',
        'Structure professional responses',
        'Practice the STAR method',
        'Improve workplace communication',
        'Build interview confidence',
        'Learn from mistakes through immediate feedback'
      ],
      interviewer: {
        name: 'Sarah Johnson',
        title: 'Hiring Manager',
        company: 'TechCorp Solutions',
        avatar: '👩‍💼'
      },
      questions: [
        {
          id: 1,
          text: "Good morning! Thanks for coming in today. Why don't you start by telling me a bit about yourself?",
          type: 'open',
          starMethod: false,
          tips: "Keep it professional and relevant. Focus on your education, skills, and what you're looking for."
        },
        {
          id: 2,
          text: "Tell me about a time when you had to work as part of a team. What was your role?",
          type: 'star',
          starMethod: true,
          tips: 'Use the STAR method: Situation, Task, Action, Result'
        },
        {
          id: 3,
          text: "Describe a challenge you faced and how you overcame it.",
          type: 'star',
          starMethod: true,
          tips: 'Focus on your problem-solving skills and resilience'
        },
        {
          id: 4,
          text: "Why are you interested in this position at our company?",
          type: 'open',
          starMethod: false,
          tips: 'Show enthusiasm and research about the company'
        },
        {
          id: 5,
          text: "What are your strengths and how would they benefit our team?",
          type: 'open',
          starMethod: false,
          tips: 'Be specific and give examples'
        },
        {
          id: 6,
          text: "Tell me about a time you made a mistake. How did you handle it?",
          type: 'star',
          starMethod: true,
          tips: 'Show accountability and learning from experience'
        },
        {
          id: 7,
          text: "Where do you see yourself in 3-5 years?",
          type: 'open',
          starMethod: false,
          tips: 'Show ambition aligned with career growth'
        },
        {
          id: 8,
          text: "Do you have any questions for me about the role or company?",
          type: 'open',
          starMethod: false,
          tips: 'Always have 2-3 thoughtful questions prepared'
        }
      ]
    },
    {
      id: 'behavioral',
      title: '🎯 Behavioral Interview Simulator',
      difficulty: 'Intermediate',
      duration: '10-15 minutes',
      description: 'Face increasingly difficult behavioral interview questions and demonstrate your ability to use the STAR method.',
      features: ['🤖 Senior Interviewer', '🎭 Branching Scenarios', '⏱️ Timed Responses', '📊 Performance Analytics'],
      objectives: [
        'Apply the STAR framework',
        'Give specific examples',
        'Explain your individual contribution',
        'Respond to follow-up questions',
        'Handle challenging interview questions',
        'Improve professional storytelling'
      ],
      interviewer: {
        name: 'Michael Chen',
        title: 'Senior HR Director',
        company: 'Global Enterprises Inc.',
        avatar: '👨‍💼'
      },
      questions: [
        {
          id: 1,
          text: "Tell me about a time when you disagreed with a supervisor or colleague. How did you handle the situation?",
          type: 'star',
          starMethod: true,
          tips: 'Show diplomacy, communication skills, and conflict resolution'
        },
        {
          id: 2,
          text: "Describe a situation where you had to meet a tight deadline under pressure. What did you do?",
          type: 'star',
          starMethod: true,
          tips: 'Emphasize time management and stress handling'
        },
        {
          id: 3,
          text: "Give me an example of when you took initiative without being asked.",
          type: 'star',
          starMethod: true,
          tips: 'Show leadership and proactive behavior'
        },
        {
          id: 4,
          text: "Tell me about a time you failed at something. What did you learn?",
          type: 'star',
          starMethod: true,
          tips: 'Demonstrate growth mindset and resilience'
        },
        {
          id: 5,
          text: "Describe a situation where you had to adapt to significant changes at work or school.",
          type: 'star',
          starMethod: true,
          tips: 'Show flexibility and adaptability'
        },
        {
          id: 6,
          text: "Tell me about a time you went above and beyond what was expected of you.",
          type: 'star',
          starMethod: true,
          tips: 'Highlight dedication and work ethic'
        },
        {
          id: 7,
          text: "Give an example of how you've handled a difficult customer or client.",
          type: 'star',
          starMethod: true,
          tips: 'Show customer service and problem-solving skills'
        },
        {
          id: 8,
          text: "Describe a time when you had to learn something new quickly. How did you approach it?",
          type: 'star',
          starMethod: true,
          tips: 'Demonstrate learning agility and resourcefulness'
        }
      ]
    }
  ];

  const startSimulation = async (scenario) => {
    setSelectedScenario(scenario);
    setCurrentView('briefing');
    
    // Initialize session
    try {
      const response = await axios.post(`${API}/simulations/start`, {
        scenario_id: scenario.id,
        scenario_type: 'interview'
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSessionData(response.data);
    } catch (error) {
      console.error('Failed to start simulation:', error);
    }
  };

  const beginInterview = () => {
    setCurrentView('simulation');
    setCurrentQuestion(0);
    setResponses([]);
    setScore(0);
    setFeedback([]);
  };

  const submitResponse = async () => {
    if (!userInput.trim()) {
      alert('Please provide an answer before continuing.');
      return;
    }

    setIsProcessing(true);
    
    const currentQ = selectedScenario.questions[currentQuestion];
    
    try {
      // Send to AI for analysis
      const response = await axios.post(`${API}/simulations/analyze-response`, {
        session_id: sessionData?.session_id,
        question_id: currentQ.id,
        question_text: currentQ.text,
        user_response: userInput,
        use_star_method: currentQ.starMethod
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      const analysis = response.data;
      
      // Add to responses
      const newResponse = {
        question: currentQ.text,
        answer: userInput,
        analysis: analysis,
        timestamp: new Date()
      };
      
      setResponses([...responses, newResponse]);
      setFeedback([...feedback, analysis.feedback]);
      setScore(score + (analysis.score || 0));
      
      // Move to next question or results
      if (currentQuestion < selectedScenario.questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setUserInput('');
      } else {
        // Complete simulation
        await completeSimulation(analysis.overall_score);
      }
    } catch (error) {
      console.error('Error analyzing response:', error);
      alert('Failed to analyze response. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const completeSimulation = async (finalScore) => {
    try {
      await axios.post(`${API}/simulations/complete`, {
        session_id: sessionData?.session_id,
        final_score: finalScore,
        responses: responses
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setCurrentView('results');
    } catch (error) {
      console.error('Error completing simulation:', error);
    }
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [responses, currentQuestion]);

  // Menu View
  if (currentView === 'menu') {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">💼 Interview Simulation Center</h1>
            <p className="text-gray-600 mt-2">Practice real-world job interviews with AI-powered feedback</p>
          </div>
          <button
            onClick={onBack}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
          >
            ← Back to Dashboard
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {scenarios.map((scenario) => (
            <div
              key={scenario.id}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all transform hover:-translate-y-1"
            >
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
                <h2 className="text-2xl font-bold mb-2">{scenario.title}</h2>
                <div className="flex items-center gap-4 text-sm">
                  <span className={`px-3 py-1 rounded-full ${
                    scenario.difficulty === 'Beginner' ? 'bg-green-500' : 'bg-yellow-500'
                  }`}>
                    {scenario.difficulty}
                  </span>
                  <span>⏱️ {scenario.duration}</span>
                </div>
              </div>

              <div className="p-6">
                <p className="text-gray-700 mb-4">{scenario.description}</p>

                <div className="space-y-3 mb-6">
                  {scenario.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <span className="text-blue-600">✓</span>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-blue-50 p-4 rounded-lg mb-6">
                  <h4 className="font-bold text-sm mb-2">📚 Learning Objectives:</h4>
                  <ul className="space-y-1 text-sm">
                    {scenario.objectives.map((obj, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-blue-600">•</span>
                        <span>{obj}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => startSimulation(scenario)}
                  className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg"
                >
                  LAUNCH SIMULATION →
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* STAR Method Quick Reference */}
        <div className="mt-8 bg-gradient-to-br from-yellow-50 to-orange-50 p-6 rounded-xl border-2 border-yellow-400">
          <h3 className="text-xl font-bold text-gray-800 mb-4">⭐ The STAR Method</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg">
              <h4 className="font-bold text-blue-600 mb-2">S - Situation</h4>
              <p className="text-sm">Describe the context or background</p>
            </div>
            <div className="bg-white p-4 rounded-lg">
              <h4 className="font-bold text-purple-600 mb-2">T - Task</h4>
              <p className="text-sm">Explain what needed to be done</p>
            </div>
            <div className="bg-white p-4 rounded-lg">
              <h4 className="font-bold text-green-600 mb-2">A - Action</h4>
              <p className="text-sm">Detail the steps you took</p>
            </div>
            <div className="bg-white p-4 rounded-lg">
              <h4 className="font-bold text-orange-600 mb-2">R - Result</h4>
              <p className="text-sm">Share the outcome and what you learned</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Briefing View
  if (currentView === 'briefing') {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-8 text-white text-center">
            <h1 className="text-3xl font-bold mb-2">📋 Interview Briefing</h1>
            <p className="opacity-90">{selectedScenario.title}</p>
          </div>

          <div className="p-8">
            <div className="flex items-center gap-6 mb-8 bg-gray-50 p-6 rounded-xl">
              <div className="text-6xl">{selectedScenario.interviewer.avatar}</div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">{selectedScenario.interviewer.name}</h2>
                <p className="text-gray-600">{selectedScenario.interviewer.title}</p>
                <p className="text-sm text-gray-500">{selectedScenario.interviewer.company}</p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-blue-50 p-6 rounded-xl">
                <h3 className="font-bold text-lg mb-3 text-blue-800">📌 What to Expect</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span>You'll answer {selectedScenario.questions.length} interview questions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span>Each response will be analyzed by AI for content, structure, and professionalism</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span>You'll receive immediate feedback after each answer</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span>At the end, you'll get a performance score and certificate</span>
                  </li>
                </ul>
              </div>

              <div className="bg-yellow-50 p-6 rounded-xl border-l-4 border-yellow-500">
                <h3 className="font-bold text-lg mb-3 text-yellow-800">💡 Pro Tips</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-600">✓</span>
                    <span>Take your time to think before answering</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-600">✓</span>
                    <span>Use the STAR method for behavioral questions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-600">✓</span>
                    <span>Be specific with examples from your experience</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-600">✓</span>
                    <span>Show enthusiasm and confidence</span>
                  </li>
                </ul>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setCurrentView('menu')}
                  className="flex-1 py-4 border-2 border-gray-300 text-gray-700 font-bold rounded-xl hover:bg-gray-50 transition-all"
                >
                  ← Back to Menu
                </button>
                <button
                  onClick={beginInterview}
                  className="flex-1 py-4 bg-gradient-to-r from-green-500 to-blue-500 text-white font-bold rounded-xl hover:from-green-600 hover:to-blue-600 transition-all shadow-lg"
                >
                  I'm Ready! Start Interview →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Simulation View
  if (currentView === 'simulation') {
    const currentQ = selectedScenario.questions[currentQuestion];
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="container mx-auto px-4 py-8 max-w-5xl">
          {/* Header */}
          <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="text-4xl">{selectedScenario.interviewer.avatar}</div>
                <div>
                  <h2 className="font-bold text-lg">{selectedScenario.interviewer.name}</h2>
                  <p className="text-sm text-gray-600">{selectedScenario.interviewer.title}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Question {currentQuestion + 1} of {selectedScenario.questions.length}</p>
                <div className="w-48 h-2 bg-gray-200 rounded-full mt-2">
                  <div 
                    className="h-full bg-blue-600 rounded-full transition-all"
                    style={{ width: `${((currentQuestion + 1) / selectedScenario.questions.length) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Interview Room */}
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden mb-6">
            {/* Virtual Office Background */}
            <div className="relative h-64 bg-gradient-to-b from-gray-700 to-gray-900 flex items-center justify-center">
              <div className="absolute inset-0 opacity-20">
                <div className="h-full flex items-center justify-center text-9xl">
                  🏢
                </div>
              </div>
              <div className="relative text-center text-white z-10">
                <div className="text-8xl mb-4 animate-pulse">{selectedScenario.interviewer.avatar}</div>
                <div className="bg-black bg-opacity-50 px-6 py-3 rounded-full">
                  <p className="text-sm">🎙️ Speaking...</p>
                </div>
              </div>
            </div>

            {/* Question Panel */}
            <div className="p-8">
              {currentQ.starMethod && (
                <div className="mb-4 flex items-center gap-2">
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-bold">
                    ⭐ STAR Method Question
                  </span>
                  <button
                    onClick={() => setShowSTARGuide(!showSTARGuide)}
                    className="text-blue-600 text-sm hover:underline"
                  >
                    {showSTARGuide ? 'Hide' : 'Show'} STAR Guide
                  </button>
                </div>
              )}

              {showSTARGuide && currentQ.starMethod && (
                <div className="mb-6 bg-yellow-50 p-4 rounded-lg border-2 border-yellow-300">
                  <h4 className="font-bold mb-2">⭐ STAR Framework:</h4>
                  <div className="grid grid-cols-4 gap-2 text-sm">
                    <div><strong>S:</strong> Situation</div>
                    <div><strong>T:</strong> Task</div>
                    <div><strong>A:</strong> Action</div>
                    <div><strong>R:</strong> Result</div>
                  </div>
                </div>
              )}

              <div className="bg-blue-50 p-6 rounded-xl mb-6 border-l-4 border-blue-600">
                <p className="text-xl text-gray-800 leading-relaxed">
                  "{currentQ.text}"
                </p>
              </div>

              {currentQ.tips && (
                <div className="mb-6 bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <span className="font-bold text-green-700">💡 Tip:</span> {currentQ.tips}
                  </p>
                </div>
              )}

              {/* Response Input */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Your Response:
                </label>
                <textarea
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  placeholder="Type your answer here... Be specific and detailed."
                  className="w-full p-4 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none min-h-32"
                  disabled={isProcessing}
                />
                <div className="flex items-center justify-between mt-2">
                  <p className="text-sm text-gray-500">
                    {userInput.length} characters
                  </p>
                  <button
                    onClick={submitResponse}
                    disabled={isProcessing || !userInput.trim()}
                    className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isProcessing ? 'Analyzing...' : currentQuestion < selectedScenario.questions.length - 1 ? 'Submit Answer →' : 'Finish Interview'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Previous Responses */}
          {responses.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="font-bold text-lg mb-4">📝 Your Previous Answers</h3>
              <div className="space-y-4">
                {responses.map((resp, idx) => (
                  <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm font-bold text-gray-700 mb-2">Q{idx + 1}: {resp.question}</p>
                    <p className="text-sm text-gray-600 mb-2">{resp.answer}</p>
                    {resp.analysis && (
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          resp.analysis.score >= 80 ? 'bg-green-100 text-green-700' :
                          resp.analysis.score >= 60 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          Score: {resp.analysis.score}/100
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
      </div>
    );
  }

  // Results View
  if (currentView === 'results') {
    const avgScore = responses.reduce((acc, r) => acc + (r.analysis?.score || 0), 0) / responses.length;
    const passed = avgScore >= 70;

    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
          <div className={`p-8 text-white text-center ${
            passed ? 'bg-gradient-to-r from-green-500 to-blue-500' : 'bg-gradient-to-r from-orange-500 to-red-500'
          }`}>
            <div className="text-6xl mb-4">{passed ? '🎉' : '📚'}</div>
            <h1 className="text-3xl font-bold mb-2">
              {passed ? 'Congratulations!' : 'Interview Complete'}
            </h1>
            <p className="text-xl opacity-90">
              Your Score: {Math.round(avgScore)}/100
            </p>
          </div>

          <div className="p-8">
            {/* Performance Breakdown */}
            <div className="mb-8">
              <h3 className="text-xl font-bold mb-4">📊 Performance Breakdown</h3>
              <div className="space-y-4">
                {responses.map((resp, idx) => (
                  <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-gray-700">Question {idx + 1}</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                        resp.analysis.score >= 80 ? 'bg-green-100 text-green-700' :
                        resp.analysis.score >= 60 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {resp.analysis.score}/100
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{resp.question}</p>
                    {resp.analysis.feedback && (
                      <div className="mt-3 bg-white p-3 rounded border-l-4 border-blue-500">
                        <p className="text-sm"><strong>Feedback:</strong> {resp.analysis.feedback}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Certificate */}
            {passed && (
              <div className="mb-8 bg-gradient-to-br from-yellow-50 to-orange-50 p-8 rounded-xl border-4 border-yellow-400 text-center">
                <div className="text-6xl mb-4">🏆</div>
                <h2 className="text-2xl font-bold mb-2">Certificate of Completion</h2>
                <p className="text-gray-700 mb-4">
                  {user?.first_name} {user?.last_name} has successfully completed<br/>
                  <strong>{selectedScenario.title}</strong>
                </p>
                <p className="text-sm text-gray-600">
                  Score: {Math.round(avgScore)}/100 | Date: {new Date().toLocaleDateString()}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-4">
              <button
                onClick={() => setCurrentView('menu')}
                className="flex-1 py-4 border-2 border-gray-300 text-gray-700 font-bold rounded-xl hover:bg-gray-50 transition-all"
              >
                ← Back to Menu
              </button>
              <button
                onClick={() => {
                  setCurrentView('briefing');
                  setCurrentQuestion(0);
                  setResponses([]);
                  setScore(0);
                  setFeedback([]);
                }}
                className="flex-1 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg"
              >
                Try Again 🔄
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default InterviewSimulator;
